import styled from '@emotion/styled';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { device } from 'utils';
import projectServices from '../../../../../services/ProjectsServices';
import WhatsappIcon from './Icons/WhatsappIcon';
import InstagramIcon from './Icons/InstagramIcon';
import FacebookIcon from './Icons/FacebookIcon';
import LinkedinIcon from './Icons/LinkedinIcon';
import LinkIcon from './Icons/LinkIcon';
import WidgetIcon from './Icons/WidgetIcon';
import CopyIcon from './Icons/CopyIcon';
const ShareProject = ({ project }) => {
  const [copied, setCopied] = useState(false);
  const [instagramCopied, setInstagramCopied] = useState(false);
  const [facebookCopied, setFacebookCopied] = useState(false);
  const [linkedinCopied, setLinkedinCopied] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const projectId = project?.id;

  const recordShare = (channel) => {
    if (!projectId) return;

    projectServices.shareProject(projectId, channel).catch(() => {});
  };

  const copyToClipboard = async (str, channel = 'copy') => {
    recordShare(channel);

    try {
      await navigator.clipboard.writeText(str);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = str;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = getProjectShareText(currentUrl);

  const openShareWindow = (shareUrl) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const url = new URL(currentUrl);

  url.pathname = `${url.pathname.replace(/\/$/, '')}/widget`;

  const widgetUrl = url.toString();

  const iframeCode = `<iframe src="${widgetUrl}" width="100%" height="200" style="border:none;border-radius:12px;overflow:hidden;margin:0; padding: 0;" loading="lazy"></iframe>`;

  return (
    <ShareContentWrapper>
      <Content>
        <TextBlock>
          <Title>Teile dieses Projekt</Title>
          <Subtitle>
            Gemeinsam spenden macht mehr Sinn –{' '}
            <strong>
              dein Teilen könnte 34 € mehr Unterstützung bedeuten!
            </strong>
          </Subtitle>
        </TextBlock>

        <ButtonGroup>
          <BaseButton
            type="button"
            onClick={async () => {
              await copyToClipboard(shareText);
              setCopied(true);
            }}
          >
            <LinkIcon copied={copied} />
            <ButtonText $copied={copied}>
              {copied ? 'Text kopiert' : 'Text kopieren'}
            </ButtonText>
          </BaseButton>
          <BaseButton
            type="button"
            onClick={() => {
              recordShare('whatsapp');
              openShareWindow(
                `https://api.whatsapp.com/send?text=${encodeURIComponent(
                  shareText
                )}`
              );
            }}
          >
            <WhatsappIcon />
            <ButtonText>WhatsApp</ButtonText>
          </BaseButton>
          <BaseButton
            type="button"
            onClick={() => {
              copyToClipboard(shareText, 'instagram').then(() =>
                setInstagramCopied(true)
              );
              window.open(
                'https://www.instagram.com/',
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <InstagramIcon />
            <ButtonText $copied={instagramCopied}>
              {instagramCopied ? 'Text kopiert' : 'Instagram'}
            </ButtonText>
          </BaseButton>
          <BaseButton
            type="button"
            onClick={() => {
              copyToClipboard(shareText, 'facebook').then(() =>
                setFacebookCopied(true)
              );
              openShareWindow(
                `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  currentUrl
                )}&quote=${encodeURIComponent(
                  shareText
                )}&hashtag=${encodeURIComponent('#commonsplace')}`
              );
            }}
          >
            <FacebookIcon />
            <ButtonText $copied={facebookCopied}>
              {facebookCopied ? 'Text kopiert' : 'Facebook'}
            </ButtonText>
          </BaseButton>
          <BaseButton
            type="button"
            onClick={() => {
              copyToClipboard(shareText, 'linkedin').then(() =>
                setLinkedinCopied(true)
              );
              openShareWindow(
                `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                  currentUrl
                )}&title=${encodeURIComponent(
                  project?.title || 'commonsplace Projekt'
                )}&summary=${encodeURIComponent(
                  shareText
                )}&source=${encodeURIComponent('commonsplace')}`
              );
            }}
          >
            <LinkedinIcon />
            <ButtonText $copied={linkedinCopied}>
              {linkedinCopied ? 'Text kopiert' : 'LinkedIn'}
            </ButtonText>
          </BaseButton>
          <BaseButton
            type="button"
            onClick={() => {
              recordShare('widget');
              setIsWidgetOpen(!isWidgetOpen);
            }}
          >
            <WidgetIcon opened={isWidgetOpen} />
            <ButtonText $copied={isWidgetOpen}>Widget einbinden</ButtonText>
          </BaseButton>
        </ButtonGroup>

        {isWidgetOpen ? (
          <WidgetSection>
            <WidgetTitleText>Quelltext</WidgetTitleText>
            <WidgetTextArea
              readOnly={true}
              disabled={true}
              value={iframeCode}
            ></WidgetTextArea>

            <WidgetCopy
              role="button"
              tabIndex={0}
              onClick={async () => {
                await copyToClipboard(iframeCode);
                setCopied(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  copyToClipboard(iframeCode).then(() => setCopied(true));
                }
              }}
            >
              <CopyIcon /> {copied ? 'Quelltext kopiert' : 'Quelltext kopieren'}
            </WidgetCopy>
            <WidgetHelpText>
              Mit diesem Quelltext kannst du einen Teaser zu diesem
              commonsplace-Projekt auf einer anderen Webseite einbinden.
            </WidgetHelpText>
          </WidgetSection>
        ) : (
          <IllustrationPlaceholder>
            <IllustrationImage
              src="/share/share.png"
              alt="share illustration"
            />
          </IllustrationPlaceholder>
        )}
      </Content>
    </ShareContentWrapper>
  );
};

const getProjectShareText = (projectUrl) => `Ich habe dieses Projekt entdeckt und möchte es gerne mit Dir teilen:

👉 ${projectUrl}

Wenn Dich das Projekt anspricht, freue ich mich, wenn Du es unterstützt oder mit anderen teilst.

Jeder Beitrag zählt.

________________
commonsplace ist die deutsch-muslimische Crowdfunding-Plattform für Bildungsprojekte, soziale Initiativen, Moscheen, Nothilfe und viele weitere Vorhaben. Gemeinsam wurden bereits zahlreiche Projekte erfolgreich finanziert und umgesetzt.

#commonsplace #Crowdfunding #GemeinsamGutesBewirken`;

ShareProject.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  banner: PropTypes.string,
  project: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  const { project } = state.projects;
  return {
    project,
  };
};

const WidgetCopy = styled.div`
  /* Buttons/Button */

  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 16px;
  gap: 8px;

  width: 100%;
  max-width: 100%;
  min-height: 44px;

  margin-bottom: 6px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  /* Shadows/shadow-xs-skeuomorphic
  
  Used to create a subtle skeuomorphic effect in components such as buttons and tabs.
  
  This can be removed or replaced with shadow-xs if you prefer a flat style.
  */
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  border-radius: 8px;

  /* Text md/Semibold */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  color: #414651;
  cursor: pointer;

`;

const WidgetSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const ShareContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  max-width: 400px;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  margin: 0 auto;

  @media ${device.allMobile} {
    max-width: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  padding: 0 24px 24px;
  gap: 20px;
  flex: 1;
  box-sizing: border-box;

  @media ${device.allMobile} {
    padding: 0 0 12px;
    gap: 16px;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;

  @media ${device.allMobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 352px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: #535862;

  @media ${device.allMobile} {
    max-width: 100%;
    font-size: 13px;
    line-height: 18px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media ${device.allMobile} {
    gap: 10px;
  }
`;

const BaseButton = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  appearance: none;

  &:hover {
    background: #f8f9fb;
    border-color: #c7c9d0;
  }

  @media ${device.allMobile} {
    padding: 10px 12px;
  }
`;

const ButtonText = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${({ $copied }) => ($copied ? '#9DBC60' : '#414651')};

  @media ${device.allMobile} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const IllustrationPlaceholder = styled.div`
  width: 100%;
  min-height: 286px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${device.allMobile} {
    min-height: auto;
  }
`;

const IllustrationImage = styled.img`
  width: 100%;
  max-width: 350px;
  height: auto;
  display: block;
  object-fit: contain;

  @media ${device.allMobile} {
    max-width: 100%;
  }
`;

const WidgetHelpText = styled.div`

  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  /* or 143% */

  color: #535862;
  
`;

const WidgetTitleText = styled.div`
  /* Text sm/Medium */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  /* identical to box height, or 143% */
    margin-bottom: 6px;
  color: #414651;

`;

const WidgetTextArea = styled.textarea`
  /* Input */

  margin-bottom: 6px;
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 12px 14px;
  gap: 8px;
  isolation: isolate;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 177px;
  resize: vertical;
  overflow-wrap: anywhere;

  background: #ffffff;
  border: 1px solid #d5d7da;
  /* Shadows/shadow-xs
  
  Shadows allow you to add depth and realism to designs by positioning elements on a z-axis.
  */
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
  
`;
export default connect(mapStateToProps)(ShareProject);
